import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle, Loader2, MapPin, Search } from 'lucide-react';
import { useCampaignStore } from '../stores/campaignStore';
import { campaignService, CampaignStore } from '../services/campaignService';
import { useVoucherStore } from '../stores/voucherStore';
import { StepProgress } from '../components/StepProgress';
import { CampaignHeader } from '../components/CampaignHeader';
import { useAnalytics } from '../hooks/useAnalytics';

interface StateEntry {
  state: string;
  uf: string;
  cities: { name: string; la: number; lg: number }[];
}

export function Step2SelectStore() {
  const navigate = useNavigate();
  const { campaignSlug } = useParams<{ campaignSlug: string }>();
  const { campaign, loadActiveCampaign } = useCampaignStore();
  const { customer, userLocation, setCurrentStep, updateReservation } = useVoucherStore();
  const { trackException, trackSelectContent } = useAnalytics();

  const [stores, setStores] = useState<CampaignStore[]>([]);
  const [search, setSearch] = useState('');
  const [selectedStore, setSelectedStore] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [initialCoords, setInitialCoords] = useState<{ lat: number; lng: number } | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!campaign) {
      void loadActiveCampaign();
      return;
    }
    if (userLocation) {
      setInitialCoords(userLocation);
      void loadStoresByCoords(userLocation.lat, userLocation.lng);
    } else {
      void loadStoresFromCustomerCity();
    }
  }, [campaign, loadActiveCampaign, userLocation]);

  const loadStoresByCoords = async (lat: number, lng: number) => {
    if (!campaign?.id) return;
    setLoading(true);
    setError('');
    try {
      const result = await campaignService.getStores(campaign.id, undefined, undefined, { lat, lng });
      setStores(result);
    } catch {
      trackException('store_load_by_location_failed');
      setError('Não foi possível carregar as lojas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const loadStoresBySearch = async (storeSearch: string) => {
    if (!campaign?.id) return;
    setLoading(true);
    setError('');
    try {
      const result = await campaignService.getStores(campaign.id, undefined, storeSearch);
      setStores(result);
    } catch {
      trackException('store_search_failed');
      setError('Não foi possível carregar as lojas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  /** Fallback: get lat/lng from customer's city via cities_by_state.json */
  const loadStoresFromCustomerCity = async () => {
    if (!customer?.state || !customer?.city) {
      // No city info — load all stores for the campaign
      if (campaign?.id) {
        setLoading(true);
        try {
          const result = await campaignService.getStores(campaign.id);
          setStores(result);
        } catch {
          trackException('store_load_all_failed');
          setError('Não foi possível carregar as lojas.');
        } finally {
          setLoading(false);
        }
      }
      return;
    }

    try {
      const res = await fetch('/data/cities_by_state.json');
      const data: StateEntry[] = await res.json();
      const stateEntry = data.find((s) => s.uf === customer.state);
      const cityEntry = stateEntry?.cities.find(
        (c) => c.name.toUpperCase() === customer.city!.toUpperCase(),
      );

      if (cityEntry) {
        setInitialCoords({ lat: cityEntry.la, lng: cityEntry.lg });
        await loadStoresByCoords(cityEntry.la, cityEntry.lg);
      } else {
        // City not found in JSON — load all stores
        if (campaign?.id) {
          const result = await campaignService.getStores(campaign.id);
          setStores(result);
        }
      }
    } catch {
      trackException('store_customer_location_failed');
      setError('Não foi possível carregar as lojas.');
    }
  };

  const handleSearch = (storeSearch = search) => {
    trackSelectContent('search', 'buscar_loja', {
      eventCategory: 'step_2_selecao_loja',
      eventAction: 'buscar_loja',
      eventLabel: 'campo_busca_loja',
    });
    if (!storeSearch.trim()) {
      // Campo vazio: recarregar lojas por proximidade
      if (initialCoords) {
        void loadStoresByCoords(initialCoords.lat, initialCoords.lng);
      } else {
        void loadStoresFromCustomerCity();
      }
      return;
    }
    void loadStoresBySearch(storeSearch);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => handleSearch(value), 300);
  };

  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  const handleContinue = () => {
    if (!selectedStore) return;
    trackSelectContent('store', selectedStore, {
      eventCategory: 'step_2_selecao_loja',
      eventAction: 'selecionar_loja',
      eventLabel: 'loja_selecionada',
    });
    const store = stores.find((item) => item.id === selectedStore);
    updateReservation({
      storeId: selectedStore,
      storeCode: store?.code || '',
      storeName: store?.name,
      storeCity: store?.city || undefined,
      storeState: store?.state || undefined,
    });
    setCurrentStep(3);
    navigate(`/${campaignSlug}/datas`);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <CampaignHeader />

      <div className="max-w-lg mx-auto w-full px-6 pt-6">
        <StepProgress currentStep={2} />
      </div>

      <main className="max-w-lg mx-auto w-full px-6 pb-6 flex-1">
        <section className="pt-1">
            <p className="text-sm text-gray-600 mb-3">
              {userLocation
                ? <>Aqui estão as lojas <strong>participantes da campanha</strong>, próximas da sua localização. Fique à vontade para pesquisar e selecionar outra loja.</>
                : <>Aqui estão as lojas <strong>participantes da campanha</strong>, próximas de {customer?.city || 'sua localização'}. Fique à vontade para pesquisar e selecionar outra loja.</>}
            </p>

            <form className="relative" onSubmit={(event) => { event.preventDefault(); handleSearch(); }}>
              <Search size={18} className="absolute left-3 top-3 text-gray-400" />
              <input
                value={search}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder="Digite o bairro ou cidade da loja..."
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-3 pl-10 pr-3 text-sm outline-none focus:border-rchlo-green"
              />
              <button
                type="submit"
                className="mt-2 w-full rounded-lg border border-[#b7d4c4] bg-[#e8f2ec] px-4 py-2.5 text-sm font-medium text-rchlo-green hover:bg-[#dcece3]"
              >
                Buscar
              </button>
            </form>

            <div className="mt-4 max-h-[min(50vh,28rem)] space-y-2 pr-1">
              {loading && <Loader2 size={22} className="animate-spin text-rchlo-green mx-auto my-8" />}
              {!loading && error && <p className="text-sm text-red-600 text-center">{error}</p>}
              {!loading && !error && stores.slice(0, 5).map((store) => (
                <button
                  key={store.id}
                  onClick={() => setSelectedStore(store.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${selectedStore === store.id ? 'border-rchlo-green bg-[#eff7f2] ring-1 ring-rchlo-green' : 'border-gray-200 shadow-sm'}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${selectedStore === store.id ? 'bg-rchlo-green' : 'bg-rchlo-green/10'}`}>
                      <MapPin size={18} className={selectedStore === store.id ? 'text-white' : 'text-rchlo-green'} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-gray-900">{store.name}</span>
                      <span className="block text-xs text-gray-500">
                        {store.city || 'Cidade'} • {store.state || 'UF'}
                        {store.distanceKm != null && ` • ${store.distanceKm} km`}
                      </span>
                    </span>
                    {selectedStore === store.id && <CheckCircle size={21} className="shrink-0 fill-rchlo-green text-white" aria-label="Loja selecionada" />}
                  </div>
                </button>
              ))}
              {!loading && !error && stores.length > 0 && (
                <p className="px-2 py-3 text-center text-sm font-medium text-blue-600">
                  Para mais resultados, informe o bairro ou cidade da loja no campo acima!
                </p>
              )}
              {!loading && !error && stores.length === 0 && (
                <div className="text-center py-16 text-gray-400">
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <MapPin size={26} />
                  </div>
                  <p className="text-sm leading-6 max-w-xs mx-auto">
                    Nenhuma loja encontrada. Tente buscar por nome ou cidade.
                  </p>
                </div>
              )}
            </div>
        </section>
      </main>

      <footer className="max-w-lg mx-auto w-full px-6 pb-6 space-y-2">
          <button onClick={handleContinue} disabled={!selectedStore || loading} className="btn-primary w-full">
            Próximo
          </button>
          <button onClick={() => navigate(`/${campaignSlug}/cadastro`)} className="btn-secondary w-full">
            Voltar
          </button>
      </footer>
    </div>
  );
}
